import { MigrationCostSummary, MigrationResourceCosts, SprintTeamComposition } from '@amzn/awscat-portfolio-assessment-types';
import { Button, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';

import { constants } from '../../common/Localization';
import { formatCurrencyNoDecimals, getCurrencySymbol } from '../../common/LocalizationHelper';
import { deepCopy } from '../../common/util';
import { RATE, resources } from './constants';
import { SprintTeamResourceData } from './interfaces';
import MigrationSummarySprintTeamResourceEditModal from './MigrationSummarySprintTeamResourceEditModal';

interface Props {
    currency: string;
    migrationCostSummary: MigrationCostSummary[];
    migrationResourceCost: MigrationResourceCosts;
    sprintTeam: SprintTeamComposition;
    updateTco: (sprintTeam: SprintTeamComposition, resourceCosts: MigrationResourceCosts) => void;
}

function MigrationSummarySprintTeamResourceTable({ currency, migrationCostSummary, migrationResourceCost, sprintTeam, updateTco }: Props) {

    const [ isEditModalVisible, setIsEditModalVisible ] = useState(false);
    const [ tableItems, setTableItems ] = useState<SprintTeamResourceData[]>([]);
    const [ columnDefinitions, setColumnDefinitions ] = useState<TableProps.ColumnDefinition<SprintTeamResourceData>[]>([]);

    const getResourceCountName = (strategy: string, countIdentifier: string): string => {
        return countIdentifier.replace('{RType}', strategy.toLowerCase());
    };

    const getSprintTeamResourceColumnDefinitions = (): TableProps.ColumnDefinition<SprintTeamResourceData>[] => {
        const firstColumnDef: TableProps.ColumnDefinition<SprintTeamResourceData> = 
        {
            id: 'resource',
            header: constants.RESOURCE,
            cell: item => item.resource
        };
        const lastColumnDef: TableProps.ColumnDefinition<SprintTeamResourceData> = 
        {
            id: RATE,
            header: `${constants.RATE} (${getCurrencySymbol(currency)}/${constants.HOUR_ABV})`,
            cell: item => formatCurrencyNoDecimals(Number(item.rate), currency)
        };
    
        const middleColumns: TableProps.ColumnDefinition<SprintTeamResourceData>[] = migrationCostSummary.map(summary => {
            return {
                id: summary.strategy,
                header: summary.strategy,
                cell: item => item.strategies[summary.strategy] ?? ''
            };
        });
    
        return [firstColumnDef, ...middleColumns, lastColumnDef];
    };

    useEffect(() => {
        const columnDefinitions = getSprintTeamResourceColumnDefinitions();
        setColumnDefinitions(columnDefinitions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [migrationCostSummary]);

    useEffect(() => {
        if (!resources || resources.length === 0) {
            return;
        }

        const generateItems = (): void => {
            const data: SprintTeamResourceData[] = resources.map(resource => {
                const strategies: {[key: string]: number} = {};
                migrationCostSummary.forEach(summary => {
                    const col = getResourceCountName(summary.strategy, resource.countIdentifier);
                    if (sprintTeam.hasOwnProperty(col)) {
                        strategies[summary.strategy] = sprintTeam[col as keyof SprintTeamComposition] ?? '';
                    }
                });
                const rate = migrationResourceCost[resource.costIdentifier] ?? 0;
                const resourceData: SprintTeamResourceData = {
                    resource: resource.name,
                    strategies,
                    rate
                };
                return resourceData;
            });
            setTableItems(data);
        };
        generateItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sprintTeam, migrationResourceCost]);

    const mapDataForTcoUpdate = (sprintTeamData: SprintTeamResourceData[]) => {
        let migrationSprintTeam: SprintTeamComposition = deepCopy(sprintTeam);
        let resourceCosts: MigrationResourceCosts = deepCopy(migrationResourceCost);

        resources.forEach(resource => {
            const item = sprintTeamData.find(item => item.resource === resource.name);
            if (!item) {
                return;
            }
            migrationCostSummary.forEach(summary => {
                const col = getResourceCountName(summary.strategy, resource.countIdentifier);
                if (sprintTeam.hasOwnProperty(col)) {
                    migrationSprintTeam = {...migrationSprintTeam, [col as keyof SprintTeamComposition]: Number(item.strategies[summary.strategy])};
                }
            });
            resourceCosts = {...resourceCosts, [resource.costIdentifier]: Number(item.rate)};
        });
        updateTco(migrationSprintTeam, resourceCosts);
    };

    const editButton = (
        <Button variant='primary' onClick={() => setIsEditModalVisible(true)}>{constants.EDIT_BUTTON_TEXT}</Button>
    );
    return (
        <SpaceBetween size='s'>
            <Table
                columnDefinitions={columnDefinitions}
                items={tableItems}
                preferences={editButton}
            />
            <MigrationSummarySprintTeamResourceEditModal
                currency={currency}
                visible={isEditModalVisible}
                setVisible={setIsEditModalVisible}
                migrationCostSummary={migrationCostSummary}
                sprintTeamResourceData={tableItems}
                onRecalculate={mapDataForTcoUpdate}
            />
        </SpaceBetween>
    );
}

export default MigrationSummarySprintTeamResourceTable;
